import React, { useEffect } from "react";
import { inr, formatDateTime } from "@/lib/utils_pharma";

/**
 * Consolidated Patient Sales Report Thermal Print component.
 * Supports 58mm, 72mm, and 80mm roll sizes based on shop settings.
 */
export default function ConsolidatedPatientsThermalPrint({
  summary,
  patients = [],
  periodLabel = "",
  shop,
  preview = false,
}) {
  const paperWidth = ["58mm", "72mm", "80mm"].includes(shop?.thermal_paper_width)
    ? shop.thermal_paper_width
    : "72mm";
  const is58 = paperWidth === "58mm";

  useEffect(() => {
    document.documentElement.style.setProperty("--receipt-paper-width", paperWidth);
  }, [paperWidth]);

  const divider = "1px dashed #000";
  const classes = preview ? "invoice-preview" : "print-area print-only invoice-preview";

  const style = {
    width: paperWidth,
    minHeight: "auto",
    height: "auto",
    boxSizing: "border-box",
    padding: is58 ? "1.5mm" : "2.5mm",
    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    fontSize: is58 ? 8.5 : 9.5,
    lineHeight: 1.3,
    background: "#fff",
    color: "#000",
    border: preview ? "1px dashed #ccc" : "none",
    margin: preview ? "0 auto" : 0,
    overflow: "visible",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const printedAt = formatDateTime(new Date());

  return (
    <div className={classes} style={style} data-testid="consolidated-patient-thermal-print">
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        {shop?.logo_data_url && (
          <img
            src={shop.logo_data_url}
            alt="logo"
            style={{
              maxWidth: is58 ? "30mm" : "40mm",
              maxHeight: "18mm",
              display: "block",
              margin: "0 auto 2px",
              filter: "grayscale(1)",
            }}
          />
        )}
        <div
          style={{
            fontWeight: 800,
            fontSize: is58 ? 11 : 12.5,
            letterSpacing: 0.2,
            textTransform: "uppercase",
          }}
        >
          {shop?.name || shop?.shop_name || "Pharmacy"}
        </div>
        {shop?.address && (
          <div style={{ fontSize: is58 ? 7.5 : 8.5, lineHeight: 1.2 }}>{shop.address}</div>
        )}
        <div
          style={{
            fontWeight: 700,
            fontSize: is58 ? 9.5 : 10.5,
            marginTop: 3,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Patient Sales Report
        </div>
        <div style={{ fontSize: is58 ? 8 : 9, marginTop: 1, color: "#333" }}>
          {periodLabel || "Today"}
        </div>
      </div>

      {/* Table Header */}
      <div
        style={{
          borderTop: divider,
          borderBottom: divider,
          padding: "3px 0",
          fontSize: is58 ? 8 : 8.5,
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: is58 ? "1fr 10mm 18mm" : "1fr 14mm 22mm",
            columnGap: 2,
          }}
        >
          <span>PATIENT</span>
          <span style={{ textAlign: "center" }}>BILLS</span>
          <span style={{ textAlign: "right" }}>FINAL</span>
        </div>
      </div>

      {/* Patient Rows */}
      <div style={{ fontSize: is58 ? 8 : 9, lineHeight: 1.3 }}>
        {patients.length === 0 ? (
          <div style={{ padding: "4px 0", textAlign: "center", color: "#444", fontStyle: "italic" }}>
            No patient activity
          </div>
        ) : (
          patients.map((p, idx) => (
            <div
              key={p.patient_id || idx}
              style={{
                display: "grid",
                gridTemplateColumns: is58 ? "1fr 10mm 18mm" : "1fr 14mm 22mm",
                columnGap: 2,
                padding: "2px 0",
                borderBottom: idx === patients.length - 1 ? "none" : "1px dotted #888",
              }}
            >
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span style={{ fontWeight: 600 }}>{p.patient_name || "—"}</span>
              </div>
              <div style={{ textAlign: "center" }}>{p.bill_count}</div>
              <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>{inr(p.final_sales)}</div>
            </div>
          ))
        )}
      </div>

      {/* Summary Totals */}
      <div
        style={{
          borderTop: divider,
          borderBottom: divider,
          padding: "4px 0",
          marginTop: 4,
          fontSize: is58 ? 8.5 : 9.5,
          lineHeight: 1.35,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Patients:</span>
          <span style={{ fontWeight: 600 }}>{summary?.total_patients ?? patients.length}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Bills:</span>
          <span style={{ fontWeight: 600 }}>{summary?.total_bills ?? 0}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Total Sales:</span>
          <span style={{ fontWeight: 600 }}>{inr(summary?.total_sales ?? 0)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Total Returns:</span>
          <span style={{ fontWeight: 600 }}>{inr(summary?.total_returns ?? 0)}</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 800,
            fontSize: is58 ? 9 : 10,
            borderTop: "1px dotted #000",
            paddingTop: 2,
            marginTop: 2,
          }}
        >
          <span>Final Sales:</span>
          <span>{inr(summary?.final_sales ?? 0)}</span>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          textAlign: "center",
          fontSize: is58 ? 7.5 : 8,
          color: "#444",
          marginTop: 6,
        }}
      >
        <div>Printed: {printedAt}</div>
      </div>
    </div>
  );
}
